import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";

const SECTIONS_FR = [
  {
    id: "mentions",
    eyebrow: "I",
    title: "Mentions légales",
    intro:
      "Informations relatives à l'éditeur, à l'hébergement et aux droits attachés au site pulaar.co.",
    blocks: [
      {
        h: "Éditeur du site",
        text:
          "Le site pulaar.co est édité par PULAAR STUDIO, société par actions simplifiée à associé unique au capital social de 5 000,00 €, immatriculée au Registre du Commerce et des Sociétés de Paris sous le numéro 107 888 190 (SIRET : 107 888 190 00017). Siège social : 21 place de la République, 75003 Paris, France. Email : info@pulaar.co.",
      },
      {
        h: "Directeur de la publication",
        text: "Monsieur Moustapha Bachir Mbaye, Président de Pulaar Studio.",
      },
      {
        h: "Hébergement",
        text:
          "Le site pulaar.co est hébergé via la plateforme Emergent (emergent.sh), s'appuyant sur l'infrastructure Google Cloud Platform — Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irlande.",
      },
      {
        h: "Propriété intellectuelle",
        text:
          "L'ensemble des éléments présents sur le site pulaar.co — notamment les textes, images, photographies, logos, graphismes, vidéos, créations textiles, silhouettes, noms de produits et contenus éditoriaux — sont protégés par le droit de la propriété intellectuelle. Toute reproduction, représentation, modification, adaptation, diffusion ou exploitation totale ou partielle du site ou de ses contenus, sans autorisation écrite préalable de Pulaar, est interdite.",
      },
      {
        h: "Responsabilité",
        text:
          "Pulaar s'efforce d'assurer l'exactitude des informations diffusées sur le site. Toutefois, des erreurs ou omissions peuvent survenir. Les photographies, descriptions et informations produits sont présentées à titre informatif. De légères variations peuvent exister, notamment en raison du caractère artisanal, textile ou limité de certaines pièces.",
      },
      {
        h: "Contact",
        text: "Pour toute demande, écrire à info@pulaar.co.",
      },
    ],
  },

  {
    id: "terms",
    eyebrow: "II",
    title: "Conditions générales de vente",
    intro:
      "Dernière mise à jour : août 2026. Les présentes Conditions Générales de Vente régissent les ventes réalisées sur le site pulaar.co entre Pulaar Studio (coordonnées complètes dans les mentions légales) et toute personne effectuant un achat ou une commande sur le site. Pulaar propose des vêtements, accessoires et pièces en petite série, disponibles en stock limité ou réalisés sur commande.",
    blocks: [
      {
        h: "1. Objet",
        text:
          "Les présentes CGV régissent les ventes réalisées sur pulaar.co entre Pulaar Studio et toute personne effectuant un achat ou une commande sur le site.",
      },
      {
        h: "2. Produits",
        text:
          "Les produits présentés sur le site sont décrits avec le plus grand soin. Chaque fiche produit indique les caractéristiques principales de la pièce : nom, description, matière, prix, disponibilité, modalités de commande et, le cas échéant, délai de confection. En raison du caractère artisanal ou textile de certaines pièces, de légères variations de matière, de texture, de couleur ou de finition peuvent exister. Ces variations ne constituent pas un défaut dès lors qu'elles relèvent de la nature même du produit ou de sa fabrication.",
      },
      {
        h: "3. Prix",
        text:
          "Les prix sont indiqués en euros (€), toutes taxes comprises (TTC), TVA française incluse au taux en vigueur. Pulaar se réserve le droit de modifier ses prix à tout moment. Le prix applicable est celui affiché au moment de la commande.",
      },
      {
        h: "4. Commande",
        text:
          "Le client sélectionne le produit souhaité, la taille disponible sur la page produit, puis valide sa commande. Certaines pièces peuvent être disponibles sur commande ou en commande spéciale. Dans ce cas, le client est informé du délai estimatif avant validation. La commande devient définitive après confirmation du paiement.",
      },
      {
        h: "5. Paiement",
        text:
          "Le paiement s'effectue en ligne par carte bancaire via Stripe. Pulaar ne conserve aucune donnée bancaire. Les informations de paiement sont traitées de manière sécurisée par Stripe.",
      },
      {
        h: "6. Commandes sur demande",
        text:
          "Certaines pièces peuvent être réalisées sur commande, en petite série ou selon disponibilité des matières. Le délai indicatif de confection est de 3 à 4 semaines, sauf indication contraire sur la fiche produit ou dans l'email de confirmation. Une commande sur demande dans une taille standard Pulaar ne constitue pas automatiquement une pièce personnalisée. En revanche, lorsqu'une pièce est réalisée selon les spécifications particulières du client, modifiée, ajustée ou clairement personnalisée, le droit de rétractation ne s'applique pas.",
      },
      {
        h: "7. Livraison",
        items: [
          "Pièce disponible : expédition sous 2 à 3 semaines.",
          "Pièce sur commande : confection estimée sous 3 à 4 semaines, puis expédition.",
          "Commande spéciale : délai communiqué au client avant confirmation.",
        ],
        footer:
          "Pulaar livre les commandes à l'adresse indiquée par le client lors de la commande. Les frais de livraison sont calculés au moment de la commande selon l'adresse de livraison et le mode d'expédition choisi.",
      },
      {
        h: "8. Retours et droit de rétractation",
        items: [
          "Non porté.",
          "Non lavé.",
          "Non abîmé.",
          "Avec ses étiquettes si applicable.",
          "Dans un emballage adapté.",
        ],
        footer:
          "Le client consommateur dispose d'un délai de 14 jours à compter de la réception du produit pour exercer son droit de rétractation, sauf exceptions légales. Pour effectuer un retour, le client doit écrire à info@pulaar.co. Les frais de retour sont à la charge du client, sauf erreur de Pulaar ou produit défectueux. Aucun retour ne doit être effectué sans demande préalable.",
      },
      {
        h: "9. Exceptions au droit de rétractation",
        text:
          "Le droit de rétractation ne s'applique pas aux produits confectionnés selon les spécifications du client ou nettement personnalisés. Cela concerne notamment les pièces réalisées sur mesure, modifiées à la demande du client, personnalisées, brodées, ajustées spécifiquement ou produites selon des indications particulières du client.",
      },
      {
        h: "10. Remboursement",
        text:
          "Après réception et vérification du produit retourné, Pulaar procède au remboursement via le moyen de paiement utilisé lors de la commande.",
      },
      {
        h: "11. Échanges",
        text:
          "Les échanges sont possibles sous réserve de disponibilité. En raison des petites séries, certaines pièces peuvent ne pas être échangeables dans une autre taille ou variante.",
      },
      {
        h: "12. Garanties légales",
        text:
          "Les produits vendus par Pulaar bénéficient des garanties légales applicables, notamment la garantie légale de conformité et la garantie contre les vices cachés. En cas de produit non conforme ou défectueux, le client peut contacter Pulaar à info@pulaar.co.",
      },
      {
        h: "13. Médiation de la consommation",
        text:
          "Conformément aux dispositions du Code de la consommation, le client consommateur peut recourir gratuitement à un médiateur de la consommation après avoir adressé une réclamation écrite préalable à Pulaar et en l'absence de réponse satisfaisante. Médiateur proposé : CM2C — Centre de la Médiation de la Consommation de Conciliateurs de Justice. La saisine du médiateur ne peut intervenir qu'après une démarche écrite préalable auprès de Pulaar à l'adresse info@pulaar.co.",
      },
      {
        h: "14. Droit applicable",
        text:
          "Les présentes Conditions Générales de Vente sont soumises au droit français. En cas de litige, une solution amiable sera recherchée en priorité.",
      },
    ],
  },

  {
    id: "privacy",
    eyebrow: "III",
    title: "Politique de confidentialité",
    intro:
      "Dernière mise à jour : août 2026. Pulaar accorde une importance particulière à la protection des données personnelles.",
    blocks: [
      {
        h: "1. Responsable du traitement",
        text:
          "Le responsable du traitement est Pulaar Studio, dont les coordonnées complètes figurent dans les mentions légales. Email : info@pulaar.co.",
      },
      {
        h: "2. Données collectées",
        items: [
          "Nom et prénom.",
          "Adresse email.",
          "Adresse de livraison.",
          "Adresse de facturation.",
          "Numéro de téléphone si renseigné.",
          "Informations liées à la commande.",
          "Messages envoyés via le formulaire de contact.",
          "Données de navigation nécessaires au fonctionnement du site.",
          "Données liées au paiement, traitées par Stripe.",
        ],
        footer: "Pulaar ne stocke pas les informations bancaires complètes des clients.",
      },
      {
        h: "3. Finalités",
        items: [
          "Traiter les commandes.",
          "Gérer les livraisons.",
          "Répondre aux demandes de contact.",
          "Envoyer les confirmations de commande.",
          "Gérer les retours et le service client.",
          "Respecter les obligations comptables et légales.",
          "Améliorer le fonctionnement du site.",
          "Envoyer une newsletter si l'utilisateur y a consenti.",
        ],
      },
      {
        h: "4. Base légale",
        items: [
          "Exécution du contrat pour les commandes.",
          "Consentement pour la newsletter ou certains cookies.",
          "Intérêt légitime pour la gestion du service client.",
          "Respect d'obligations légales, notamment comptables.",
        ],
      },
      {
        h: "5. Destinataires",
        items: [
          "Hébergeur du site.",
          "Prestataire de paiement Stripe.",
          "Prestataire d'email Resend.",
          "Transporteurs.",
          "Prestataires techniques.",
          "Expert-comptable ou administrations lorsque la loi l'impose.",
        ],
        footer:
          "Les données peuvent être transmises uniquement aux prestataires nécessaires au fonctionnement du site et au traitement des commandes.",
      },
      {
        h: "6. Durée de conservation",
        text:
          "Les données sont conservées pendant la durée nécessaire aux finalités pour lesquelles elles sont collectées. Les données liées aux commandes et factures peuvent être conservées pendant la durée légale nécessaire au respect des obligations comptables et fiscales. Les données liées à la newsletter sont conservées jusqu'au désabonnement ou retrait du consentement.",
      },
      {
        h: "7. Droits des utilisateurs",
        items: [
          "Droit d'accès.",
          "Droit de rectification.",
          "Droit d'effacement.",
          "Droit d'opposition.",
          "Droit à la limitation du traitement.",
          "Droit à la portabilité.",
          "Droit de retirer son consentement à tout moment lorsque le traitement repose sur le consentement.",
        ],
        footer:
          "Pour exercer ses droits, l'utilisateur peut écrire à info@pulaar.co. L'utilisateur peut également introduire une réclamation auprès de la CNIL.",
      },
      {
        h: "8. Sécurité",
        text:
          "Pulaar met en œuvre des mesures raisonnables pour protéger les données personnelles contre l'accès non autorisé, la perte, l'altération ou la divulgation.",
      },
      {
        h: "9. Transferts hors Union européenne",
        text:
          "Certains prestataires techniques peuvent traiter des données en dehors de l'Union européenne. Dans ce cas, Pulaar veille à ce que des garanties appropriées soient mises en place conformément à la réglementation applicable.",
      },
      {
        h: "10. Newsletter",
        text:
          "L'utilisateur peut s'inscrire à la newsletter Pulaar afin de recevoir des informations sur les collections, pièces, événements ou actualités. Il peut se désinscrire à tout moment via le lien prévu dans les emails ou en écrivant à info@pulaar.co.",
      },
    ],
  },

  {
    id: "cookies",
    eyebrow: "IV",
    title: "Cookies & Rétractation",
    intro:
      "Le site pulaar.co peut utiliser des cookies ou traceurs afin d'assurer son bon fonctionnement, mesurer son audience ou améliorer l'expérience utilisateur.",
    blocks: [
      {
        h: "1. Cookies strictement nécessaires",
        items: [
          "La navigation sur le site.",
          "La gestion du panier.",
          "La sécurisation du paiement.",
          "Le maintien de certaines préférences techniques.",
        ],
        footer:
          "Ces cookies ne nécessitent pas de consentement lorsqu'ils sont strictement nécessaires.",
      },
      {
        h: "2. Cookies soumis au consentement",
        items: [
          "Cookies de mesure d'audience non exemptés.",
          "Cookies marketing.",
          "Cookies de réseaux sociaux.",
          "Traceurs publicitaires.",
        ],
        footer:
          "D'autres cookies peuvent être utilisés uniquement avec le consentement de l'utilisateur.",
      },
      {
        h: "3. Gestion du consentement",
        text:
          "Lors de sa première visite, l'utilisateur peut accepter, refuser ou paramétrer les cookies non nécessaires. L'utilisateur peut modifier son choix à tout moment via le module de gestion des cookies disponible sur le site.",
      },
      {
        h: "4. Contact cookies",
        text: "Pour toute question relative aux cookies : info@pulaar.co.",
      },
      {
        h: "Formulaire de rétractation",
        text:
          "À envoyer par email à info@pulaar.co. Je vous notifie par la présente ma rétractation du contrat portant sur la vente du produit suivant — Nom du produit, Numéro de commande, Commandé le, Reçu le, Nom du client, Adresse du client, Email du client, Date. Signature du client uniquement en cas d'envoi papier.",
      },
    ],
  },
];

const SECTIONS_EN = [
  {
    id: "mentions",
    eyebrow: "I",
    title: "Legal notice",
    intro:
      "Information regarding the publisher, hosting and rights attached to the pulaar.co website.",
    blocks: [
      {
        h: "Website publisher",
        text:
          "The pulaar.co website is published by PULAAR STUDIO, a French simplified joint-stock company with a sole shareholder (SAS à associé unique) with a share capital of €5,000.00, registered with the Paris Trade and Companies Register under number 107 888 190 (SIRET: 107 888 190 00017). Registered office: 21 place de la République, 75003 Paris, France. Email: info@pulaar.co.",
      },
      {
        h: "Publication director",
        text: "Mr Moustapha Bachir Mbaye, President of Pulaar Studio.",
      },
      {
        h: "Hosting",
        text:
          "The pulaar.co website is hosted via the Emergent platform (emergent.sh), running on Google Cloud Platform infrastructure — Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland.",
      },
      {
        h: "Intellectual property",
        text:
          "All elements on the pulaar.co website — including texts, images, photographs, logos, graphics, videos, textile creations, silhouettes, product names and editorial content — are protected by intellectual property law. Any reproduction, representation, modification, adaptation, distribution or exploitation, in whole or in part, of the website or its content, without Pulaar's prior written authorisation, is prohibited.",
      },
      {
        h: "Liability",
        text:
          "Pulaar strives to ensure the accuracy of the information published on the website. However, errors or omissions may occur. Photographs, descriptions and product information are provided for informational purposes. Slight variations may exist, particularly due to the handcrafted, textile or limited nature of certain pieces.",
      },
      {
        h: "Contact",
        text: "For any request, please write to info@pulaar.co.",
      },
    ],
  },

  {
    id: "terms",
    eyebrow: "II",
    title: "Terms of sale",
    intro:
      "Last updated: August 2026. These Terms of Sale govern sales made on the pulaar.co website between Pulaar Studio (full details in the legal notice) and any person making a purchase or placing an order on the website. Pulaar offers garments, accessories and small-series pieces, available in limited stock or made to order.",
    blocks: [
      {
        h: "1. Purpose",
        text:
          "These Terms of Sale govern sales made on pulaar.co between Pulaar Studio and any person making a purchase or placing an order on the website.",
      },
      {
        h: "2. Products",
        text:
          "The products presented on the website are described with the greatest care. Each product page indicates the main characteristics of the piece: name, description, material, price, availability, ordering terms and, where applicable, production lead time. Due to the handcrafted or textile nature of certain pieces, slight variations in material, texture, colour or finish may exist. Such variations do not constitute a defect insofar as they result from the very nature of the product or its manufacture.",
      },
      {
        h: "3. Prices",
        text:
          "Prices are shown in euros (€), inclusive of all taxes (TTC), including French VAT at the applicable rate. Pulaar reserves the right to change its prices at any time. The applicable price is the one displayed at the time of the order.",
      },
      {
        h: "4. Orders",
        text:
          "The customer selects the desired product and the size available on the product page, then confirms the order. Certain pieces may be available to order or as special orders. In that case, the customer is informed of the estimated lead time before confirmation. The order becomes final once payment has been confirmed.",
      },
      {
        h: "5. Payment",
        text:
          "Payment is made online by bank card via Stripe. Pulaar does not store any banking data. Payment information is processed securely by Stripe.",
      },
      {
        h: "6. Made-to-order pieces",
        text:
          "Certain pieces may be made to order, in small series or subject to the availability of materials. The indicative production lead time is 3 to 4 weeks, unless otherwise stated on the product page or in the confirmation email. A made-to-order piece in a standard Pulaar size does not automatically constitute a personalised item. However, when a piece is made according to the customer's particular specifications, modified, adjusted or clearly personalised, the right of withdrawal does not apply.",
      },
      {
        h: "7. Delivery",
        items: [
          "Piece in stock: dispatched within 2 to 3 weeks.",
          "Made-to-order piece: estimated production within 3 to 4 weeks, followed by dispatch.",
          "Special order: lead time communicated to the customer before confirmation.",
        ],
        footer:
          "Pulaar delivers orders to the address provided by the customer at the time of the order. Delivery costs are calculated at checkout according to the delivery address and the chosen shipping method.",
      },
      {
        h: "8. Returns and right of withdrawal",
        items: [
          "Unworn.",
          "Unwashed.",
          "Undamaged.",
          "With its labels, where applicable.",
          "In suitable packaging.",
        ],
        footer:
          "Consumers have 14 days from receipt of the product to exercise their right of withdrawal, subject to legal exceptions. To make a return, the customer must write to info@pulaar.co. Return costs are borne by the customer, except in the event of an error by Pulaar or a defective product. No return should be made without a prior request.",
      },
      {
        h: "9. Exceptions to the right of withdrawal",
        text:
          "The right of withdrawal does not apply to products made to the customer's specifications or clearly personalised. This includes pieces made to measure, modified at the customer's request, personalised, embroidered, specifically adjusted or produced according to the customer's particular instructions.",
      },
      {
        h: "10. Refunds",
        text:
          "After receipt and inspection of the returned product, Pulaar issues a refund using the payment method used for the order.",
      },
      {
        h: "11. Exchanges",
        text:
          "Exchanges are possible subject to availability. Due to small production runs, certain pieces may not be exchangeable in another size or variant.",
      },
      {
        h: "12. Legal guarantees",
        text:
          "Products sold by Pulaar benefit from the applicable legal guarantees, in particular the legal guarantee of conformity and the guarantee against hidden defects. In the event of a non-conforming or defective product, the customer may contact Pulaar at info@pulaar.co.",
      },
      {
        h: "13. Consumer mediation",
        text:
          "In accordance with the French Consumer Code, consumers may have free recourse to a consumer mediator after sending a prior written complaint to Pulaar and in the absence of a satisfactory response. Proposed mediator: CM2C — Centre de la Médiation de la Consommation de Conciliateurs de Justice. The mediator may only be contacted after a prior written request has been sent to Pulaar at info@pulaar.co.",
      },
      {
        h: "14. Governing law",
        text:
          "These Terms of Sale are governed by French law. In the event of a dispute, an amicable solution will be sought as a priority.",
      },
    ],
  },

  {
    id: "privacy",
    eyebrow: "III",
    title: "Privacy policy",
    intro:
      "Last updated: August 2026. Pulaar attaches particular importance to the protection of personal data.",
    blocks: [
      {
        h: "1. Data controller",
        text:
          "The data controller is Pulaar Studio, whose full details appear in the legal notice. Email: info@pulaar.co.",
      },
      {
        h: "2. Data collected",
        items: [
          "First and last name.",
          "Email address.",
          "Delivery address.",
          "Billing address.",
          "Phone number, if provided.",
          "Order-related information.",
          "Messages sent via the contact form.",
          "Browsing data necessary for the operation of the website.",
          "Payment-related data, processed by Stripe.",
        ],
        footer: "Pulaar does not store customers' full banking information.",
      },
      {
        h: "3. Purposes",
        items: [
          "Processing orders.",
          "Managing deliveries.",
          "Responding to contact requests.",
          "Sending order confirmations.",
          "Managing returns and customer service.",
          "Complying with accounting and legal obligations.",
          "Improving the operation of the website.",
          "Sending a newsletter where the user has consented.",
        ],
      },
      {
        h: "4. Legal basis",
        items: [
          "Performance of the contract for orders.",
          "Consent for the newsletter or certain cookies.",
          "Legitimate interest for customer service management.",
          "Compliance with legal obligations, in particular accounting obligations.",
        ],
      },
      {
        h: "5. Recipients",
        items: [
          "The website host.",
          "Payment provider Stripe.",
          "Email provider Resend.",
          "Carriers.",
          "Technical service providers.",
          "Accountants or authorities where required by law.",
        ],
        footer:
          "Data may only be shared with the service providers necessary for the operation of the website and the processing of orders.",
      },
      {
        h: "6. Retention period",
        text:
          "Data is kept for the time necessary for the purposes for which it is collected. Data relating to orders and invoices may be kept for the legal period required to comply with accounting and tax obligations. Newsletter-related data is kept until unsubscription or withdrawal of consent.",
      },
      {
        h: "7. Users' rights",
        items: [
          "Right of access.",
          "Right of rectification.",
          "Right to erasure.",
          "Right to object.",
          "Right to restriction of processing.",
          "Right to data portability.",
          "Right to withdraw consent at any time where processing is based on consent.",
        ],
        footer:
          "To exercise these rights, users may write to info@pulaar.co. Users may also lodge a complaint with the CNIL (the French data protection authority).",
      },
      {
        h: "8. Security",
        text:
          "Pulaar implements reasonable measures to protect personal data against unauthorised access, loss, alteration or disclosure.",
      },
      {
        h: "9. Transfers outside the European Union",
        text:
          "Certain technical service providers may process data outside the European Union. In such cases, Pulaar ensures that appropriate safeguards are put in place in accordance with the applicable regulations.",
      },
      {
        h: "10. Newsletter",
        text:
          "Users may subscribe to the Pulaar newsletter to receive information about collections, pieces, events or news. They may unsubscribe at any time via the link provided in the emails or by writing to info@pulaar.co.",
      },
    ],
  },

  {
    id: "cookies",
    eyebrow: "IV",
    title: "Cookies & Withdrawal",
    intro:
      "The pulaar.co website may use cookies or trackers to ensure its proper functioning, measure its audience or improve the user experience.",
    blocks: [
      {
        h: "1. Strictly necessary cookies",
        items: [
          "Browsing the website.",
          "Managing the cart.",
          "Securing payment.",
          "Maintaining certain technical preferences.",
        ],
        footer:
          "These cookies do not require consent when they are strictly necessary.",
      },
      {
        h: "2. Cookies subject to consent",
        items: [
          "Non-exempt audience measurement cookies.",
          "Marketing cookies.",
          "Social media cookies.",
          "Advertising trackers.",
        ],
        footer:
          "Other cookies may only be used with the user's consent.",
      },
      {
        h: "3. Consent management",
        text:
          "On their first visit, users may accept, refuse or configure non-essential cookies. Users may change their choice at any time via the cookie management module available on the website.",
      },
      {
        h: "4. Cookie contact",
        text: "For any question relating to cookies: info@pulaar.co.",
      },
      {
        h: "Withdrawal form",
        text:
          "To be sent by email to info@pulaar.co. I hereby notify you of my withdrawal from the contract for the sale of the following product — Product name, Order number, Ordered on, Received on, Customer name, Customer address, Customer email, Date. Customer signature only if sent on paper.",
      },
    ],
  },
];

const PAGE_TEXT = {
  fr: {
    eyebrow: "— Mentions légales",
    titleA: "Cadre ",
    titleEm: "juridique",
    titleC: ".",
    intro:
      "Confidentialité, livraisons, retours, conditions générales — l'ensemble de nos engagements, à ouvrir librement.",
    chapter: "Chapitre",
    footer: "Pour toute question · info@pulaar.co",
  },
  en: {
    eyebrow: "— Legal notice",
    titleA: "Legal ",
    titleEm: "framework",
    titleC: ".",
    intro:
      "Privacy, deliveries, returns, terms of sale — all of our commitments, free to browse.",
    chapter: "Chapter",
    footer: "For any question · info@pulaar.co",
  },
};

function Block({ block }) {
  return (
    <div className="mb-7 last:mb-0">
      <h3 className="font-serif text-lg md:text-xl text-[#121212] mb-3 leading-tight">
        {block.h}
      </h3>
      {block.text && (
        <p className="text-sm md:text-[15px] leading-[1.85] text-[#4A4641]">
          {block.text}
        </p>
      )}
      {block.items && (
        <ul className="space-y-2 text-sm md:text-[15px] leading-[1.8] text-[#4A4641]">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-[#5C4033] flex-none mt-[8px] w-1 h-1 rounded-full bg-[#5C4033]" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
      {block.footer && (
        <p className="mt-4 text-sm leading-[1.8] text-[#4A4641] italic">
          {block.footer}
        </p>
      )}
    </div>
  );
}

function Section({ section, chapterLabel, isOpen, onToggle }) {
  const Icon = isOpen ? Minus : Plus;
  return (
    <div className="border-b border-[#D8D2C5]" data-testid={`legal-section-${section.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-7 md:py-9 text-left group"
        data-testid={`legal-toggle-${section.id}`}
        aria-expanded={isOpen}
      >
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-2">
            {chapterLabel} · {section.eyebrow}
          </p>
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-[#121212] leading-[1.05]">
            {section.title}
          </h2>
        </div>
        <Icon
          size={22}
          strokeWidth={1.1}
          className="text-[#5C4033] flex-none ml-6 transition-transform duration-500 group-hover:scale-110"
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-12 md:pb-16 max-w-3xl">
              <p className="text-base md:text-lg leading-[1.85] text-[#4A4641] mb-10 italic">
                {section.intro}
              </p>
              {section.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Legal() {
  const [openId, setOpenId] = useState("privacy");
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const sections = lang === "en" ? SECTIONS_EN : SECTIONS_FR;
  const txt = PAGE_TEXT[lang];

  return (
    <main
      data-testid="legal-page"
      className="relative pt-32 md:pt-40 pb-24 bg-[#FBF8EF]"
    >
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
            {txt.eyebrow}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-[40px] md:text-[68px] lg:text-[80px] leading-[0.98] tracking-[-0.02em] text-[#121212] max-w-[16ch]">
            {txt.titleA}<span className="italic font-light">{txt.titleEm}</span>{txt.titleC}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-[1.85] text-[#4A4641]">
            {txt.intro}
          </p>
        </Reveal>

        <div className="mt-16 md:mt-24 border-t border-[#D8D2C5]">
          {sections.map((s) => (
            <Section
              key={s.id}
              section={s}
              chapterLabel={txt.chapter}
              isOpen={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
        </div>

        <p className="mt-16 text-[11px] tracking-[0.28em] uppercase text-[#4A4641]">
          {txt.footer}
        </p>
      </section>
    </main>
  );
}
